/**
 * Facility Management Component
 * Manage facilities with NDIS, Aged Care, and Business Group configuration
 */

import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, Edit, Trash2, Save, X, MapPin, 
  Phone, Mail, Globe, Tag, Users, Briefcase, ChevronDown, ChevronRight,
  DoorOpen, UserCheck
} from 'lucide-react';
import FacilityService from '../../services/api/FacilityService';
import WardService from '../../services/api/WardService';
import { useToast } from '../Common/ToastProvider';
import { useConfirm } from '../Common/ConfirmDialog';
import WardManagement from './WardManagement';
import { NDIS_TYPES, AGED_CARE_TYPES, BUSINESS_GROUP_STRUCTURES } from '../../data/models/Facility';
import { facilitiesData, BUSINESS_GROUPS } from '../../data/facilitiesData';

const FacilityManagement = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [businessGroups, setBusinessGroups] = useState([]);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedFacilities, setExpandedFacilities] = useState({});
  const [showWardForm, setShowWardForm] = useState(false);
  const [editingWardId, setEditingWardId] = useState(null);
  const [selectedFacilityForWard, setSelectedFacilityForWard] = useState(null);
  const [selectedFacilityForWardData, setSelectedFacilityForWardData] = useState(null);
  const [wardFormData, setWardFormData] = useState({
    name: '',
    code: '',
    type: 'Room',
    description: '',
    capacity: 0,
    occupied: 0,
    careLevel: 'Medium',
    specialization: '',
    careType: 'general',
    staffRatio: '1:5',
    manager: '',
    floor: '',
    building: '',
    wing: '',
    status: 'active'
  });
  const [formData, setFormData] = useState({
    name: '',
    legalName: '',
    abn: '',
    address: {
      street: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Australia'
    },
    phone: '',
    email: '',
    website: '',
    facilityTypes: [],
    primaryType: 'agedcare',
    ndis: {
      enabled: false,
      ndisProviderNumber: '',
      ndisType: '',
      supportsCategories: []
    },
    agedCare: {
      enabled: false,
      providerNumber: '',
      agedCareType: '',
      beds: 0,
      careLevels: []
    },
    businessGroup: {
      id: '',
      name: ''
    },
    manager: '',
    status: 'active'
  });

  useEffect(() => {
    loadFacilities();
    loadBusinessGroups();
  }, []);

  const toggleFacilityExpansion = (facilityId) => {
    setExpandedFacilities(prev => ({
      ...prev,
      [facilityId]: !prev[facilityId]
    }));
  };

  const handleAddWard = async (facilityId) => {
    setSelectedFacilityForWard(facilityId);
    
    // Try to find facility in local array first (faster)
    let facility = facilities.find(f => f.id === facilityId);
    
    // If not found locally, load from service
    if (!facility) {
      try {
        const result = await FacilityService.getById(facilityId);
        if (result.success) {
          facility = result.data;
          setSelectedFacilityForWardData(facility);
        }
      } catch (error) {
        console.error('Error loading facility:', error);
      }
    } else {
      // Use local facility data
      setSelectedFacilityForWardData(facility);
    }
    
    // Determine default type based on facility type
    const defaultType = (() => {
      if (!facility) return 'Room';
      const isNDIS = facility.primaryType === 'ndis' || 
                     (Array.isArray(facility.facilityTypes) && facility.facilityTypes.includes('ndis')) ||
                     facility.ndis?.enabled === true;
      const isAgedCare = facility.primaryType === 'agedcare' || 
                         (Array.isArray(facility.facilityTypes) && facility.facilityTypes.includes('agedcare')) ||
                         facility.agedCare?.enabled === true;
      // Aged Care defaults to Ward, NDIS defaults to Room
      return isAgedCare && !isNDIS ? 'Ward' : 'Room';
    })();
    
    setWardFormData({
      name: '',
      code: '',
      type: defaultType,
      description: '',
      capacity: 0,
      occupied: 0,
      careLevel: 'Medium',
      specialization: '',
      careType: 'general',
      staffRatio: '1:5',
      manager: '',
      floor: '',
      building: '',
      wing: '',
      status: 'active'
    });
    setEditingWardId(null);
    setShowWardForm(true);
  };

  const handleEditWard = async (ward) => {
    setSelectedFacilityForWard(ward.facilityId);
    
    // Load facility to determine default type if ward doesn't have one
    let defaultType = ward.type;
    if (!ward.type) {
      try {
        const facility = facilities.find(f => f.id === ward.facilityId);
        if (facility) {
          const isAgedCare = facility.primaryType === 'agedcare' || 
                            (Array.isArray(facility.facilityTypes) && facility.facilityTypes.includes('agedcare')) ||
                            facility.agedCare?.enabled === true;
          defaultType = isAgedCare ? 'Ward' : 'Room';
        } else {
          defaultType = 'Room';
        }
      } catch (error) {
        console.error('Error determining default type:', error);
        defaultType = 'Room';
      }
    }
    
    setWardFormData({
      name: ward.name || '',
      code: ward.code || '',
      type: defaultType,
      description: ward.description || '',
      capacity: ward.capacity || 0,
      occupied: ward.occupied || 0,
      careLevel: ward.careLevel || 'Medium',
      specialization: ward.specialization || '',
      careType: ward.careType || 'general',
      staffRatio: ward.staffRatio || '1:5',
      manager: ward.manager || '',
      floor: ward.floor || '',
      building: ward.building || '',
      wing: ward.wing || '',
      status: ward.status || 'active'
    });
    setEditingWardId(ward.id);
    setShowWardForm(true);
  };

  const handleSaveWard = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const wardData = {
        ...wardFormData,
        facilityId: selectedFacilityForWard
      };

      let result;
      if (editingWardId) {
        result = await WardService.update(editingWardId, wardData);
      } else {
        result = await WardService.create(wardData);
      }

      if (result.success) {
        setShowWardForm(false);
        setEditingWardId(null);
        setSelectedFacilityForWard(null);
        loadFacilities();
      } else {
        setError(result.error || 'Failed to save ward');
      }
    } catch (error) {
      console.error('Error saving ward:', error);
      setError('An error occurred while saving the ward');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWard = async (wardId) => {
    if (!await confirm({ title: 'Delete Ward', message: 'Are you sure you want to delete this ward?', confirmLabel: 'Delete', variant: 'danger' })) {
      return;
    }

    try {
      const result = await WardService.delete(wardId);
      if (result.success) {
        loadFacilities();
      } else {
        setError(result.error || 'Failed to delete ward');
      }
    } catch (error) {
      console.error('Error deleting ward:', error);
      setError('An error occurred while deleting the ward');
    }
  };

  const loadFacilities = async () => {
    setLoading(true);
    setError(null);
    try {
      // Initialize will be called automatically by getAll()
      const result = await FacilityService.getAll();
      if (result.success) {
        setFacilities(result.data || []);
      } else {
        setError(result.error || 'Failed to load facilities');
        console.error('Failed to load facilities:', result.error);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
      setError(error.message || 'An error occurred while loading facilities');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessGroups = async () => {
    try {
      const result = await FacilityService.getBusinessGroups();
      if (result.success) {
        setBusinessGroups(result.data);
      } else {
        // Use static business groups
        setBusinessGroups(Object.values(BUSINESS_GROUPS));
      }
    } catch (error) {
      console.error('Error loading business groups:', error);
      setBusinessGroups(Object.values(BUSINESS_GROUPS));
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleEdit = (facility) => {
    setEditingId(facility.id);
    setFormData({
      name: facility.name,
      legalName: facility.legalName || '',
      abn: facility.abn || '',
      address: facility.address || { street: '', city: '', state: '', postcode: '', country: 'Australia' },
      phone: facility.phone || '',
      email: facility.email || '',
      website: facility.website || '',
      facilityTypes: facility.facilityTypes || [],
      primaryType: facility.primaryType || 'agedcare',
      ndis: facility.ndis || { enabled: false, ndisProviderNumber: '', ndisType: '', supportsCategories: [] },
      agedCare: facility.agedCare || { enabled: false, providerNumber: '', agedCareType: '', beds: 0, careLevels: [] },
      businessGroup: facility.businessGroup || { id: '', name: '' },
      manager: facility.manager || '',
      status: facility.status || 'active'
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const facilityData = {
        ...formData,
        address: {
          ...formData.address,
          fullAddress: `${formData.address.street}, ${formData.address.city} ${formData.address.state} ${formData.address.postcode}`
        }
      };

      const result = editingId
        ? await FacilityService.update(editingId, facilityData)
        : await FacilityService.create(facilityData);

      if (result.success) {
        setShowForm(false);
        setEditingId(null);
        setFormData({
          name: '',
          legalName: '',
          abn: '',
          address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
          phone: '',
          email: '',
          website: '',
          facilityTypes: [],
          primaryType: 'agedcare',
          ndis: { enabled: false, ndisProviderNumber: '', ndisType: '', supportsCategories: [] },
          agedCare: { enabled: false, providerNumber: '', agedCareType: '', beds: 0, careLevels: [] },
          businessGroup: { id: '', name: '' },
          manager: '',
          status: 'active'
        });
        loadFacilities();
        showToast('Facility saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving facility:', error);
      showToast('Error saving facility', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete Facility', message: 'Are you sure you want to delete this facility?', confirmLabel: 'Delete', variant: 'danger' })) return;
    
    try {
      const result = await FacilityService.delete(id);
      if (result.success) {
        loadFacilities();
      }
    } catch (error) {
      console.error('Error deleting facility:', error);
      showToast('Error deleting facility', 'error');
    }
  };

  const getNDISTypeLabel = (type) => {
    const labels = {
      'home': 'Home-based Support',
      'sda': 'Specialist Disability Accommodation (SDA)',
      'silo': 'Supported Independent Living Option (SILO)',
      'group_home': 'Group Home',
      'community': 'Community-based Services',
      'support_services': 'Support Services Provider'
    };
    return labels[type] || type;
  };

  const getAgedCareTypeLabel = (type) => {
    const labels = {
      'residential': 'Residential Aged Care',
      'home_care': 'Home Care Packages',
      'respite': 'Respite Care',
      'transitional': 'Transitional Care',
      'inhouse': 'In-House / Home Care'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <Building2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600">Loading facilities...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Facilities</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadFacilities();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Facility Management</h2>
            <p className="text-blue-100">Manage facilities, NDIS types, Aged Care types, and Business Groups</p>
          </div>
          <Building2 className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      {/* Business Groups Summary */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-indigo-600" />
          Business Groups
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {businessGroups.map((group) => (
            <div key={group.id} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">{group.name}</h4>
              <p className="text-sm text-gray-600 mb-2">ABN: {group.abn}</p>
              <p className="text-sm text-gray-600 mb-2">
                {group.facilities?.length || 0} facilities
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {group.corporateStructure || 'group'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Add Facility Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setFormData({
              name: '',
              legalName: '',
              abn: '',
              address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
              phone: '',
              email: '',
              website: '',
              facilityTypes: [],
              primaryType: 'agedcare',
              ndis: { enabled: false, ndisProviderNumber: '', ndisType: '', supportsCategories: [] },
              agedCare: { enabled: false, providerNumber: '', agedCareType: '', beds: 0, careLevels: [] },
              businessGroup: { id: '', name: '' },
              manager: '',
              status: 'active'
            });
          }}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Facility
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {editingId ? 'Edit Facility' : 'Add New Facility'}
            </h3>
            <button
              onClick={() => setShowForm(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Legal Name</label>
                  <input
                    type="text"
                    value={formData.legalName}
                    onChange={(e) => handleChange('legalName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ABN</label>
                  <input
                    type="text"
                    value={formData.abn}
                    onChange={(e) => handleChange('abn', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="11 digits"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
                  <input
                    type="text"
                    value={formData.manager}
                    onChange={(e) => handleChange('manager', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Address
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <select
                    value={formData.address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select state</option>
                    <option value="VIC">Victoria</option>
                    <option value="NSW">New South Wales</option>
                    <option value="QLD">Queensland</option>
                    <option value="SA">South Australia</option>
                    <option value="WA">Western Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="NT">Northern Territory</option>
                    <option value="ACT">Australian Capital Territory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Postcode</label>
                  <input
                    type="text"
                    value={formData.address.postcode}
                    onChange={(e) => handleAddressChange('postcode', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Facility Types */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-600" />
                Facility Types
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Type</label>
                  <select
                    value={formData.primaryType}
                    onChange={(e) => handleChange('primaryType', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="agedcare">Aged Care</option>
                    <option value="inhouse">In-House / Home Care</option>
                    <option value="hospital">Hospital</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility Types (Multiple)</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.facilityTypes.includes('agedcare')}
                        onChange={(e) => {
                          const types = e.target.checked
                            ? [...formData.facilityTypes, 'agedcare']
                            : formData.facilityTypes.filter(t => t !== 'agedcare');
                          handleChange('facilityTypes', types);
                        }}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span>Aged Care</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.facilityTypes.includes('hospital')}
                        onChange={(e) => {
                          const types = e.target.checked
                            ? [...formData.facilityTypes, 'hospital']
                            : formData.facilityTypes.filter(t => t !== 'hospital');
                          handleChange('facilityTypes', types);
                        }}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span>Hospital</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Aged Care Configuration */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Aged Care Configuration</h4>
              <div className="space-y-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.agedCare.enabled}
                    onChange={(e) => handleChange('agedCare.enabled', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="font-medium">Enable Aged Care Services</span>
                </label>
                {formData.agedCare.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider Number</label>
                      <input
                        type="text"
                        value={formData.agedCare.providerNumber}
                        onChange={(e) => handleChange('agedCare.providerNumber', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Aged Care Type</label>
                      <select
                        value={formData.agedCare.agedCareType}
                        onChange={(e) => handleChange('agedCare.agedCareType', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Select type</option>
                        <option value={AGED_CARE_TYPES.RESIDENTIAL}>Residential Aged Care</option>
                        <option value={AGED_CARE_TYPES.HOME_CARE}>Home Care Packages</option>
                        <option value={AGED_CARE_TYPES.RESPITE}>Respite Care</option>
                        <option value={AGED_CARE_TYPES.TRANSITIONAL}>Transitional Care</option>
                        <option value={AGED_CARE_TYPES.IN_HOUSE}>In-House / Home Care</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Number of Beds</label>
                      <input
                        type="number"
                        value={formData.agedCare.beds}
                        onChange={(e) => handleChange('agedCare.beds', parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Business Group */}
            <div className="border-b border-gray-200 pb-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600" />
                Business Group
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Group</label>
                  <select
                    value={formData.businessGroup.id}
                    onChange={(e) => {
                      const group = businessGroups.find(g => g.id === e.target.value);
                      handleChange('businessGroup', {
                        id: e.target.value,
                        name: group?.name || ''
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select business group</option>
                    {businessGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.businessGroup.id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Group Name</label>
                    <input
                      type="text"
                      value={formData.businessGroup.name}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Save className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Facility
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Facilities List */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">All Facilities</h3>
          <button
            onClick={async () => {
              try {
                const result = await FacilityService.removeDuplicates();
                if (result.success) {
                  if (result.removed > 0) {
                    showToast(`Removed ${result.removed} duplicate facilities. Kept ${result.kept} unique facilities.`, 'success');
                    loadFacilities();
                  } else {
                    showToast('No duplicates found. All facilities are unique.', 'info');
                  }
                } else {
                  setError(result.error || 'Failed to remove duplicates');
                }
              } catch (error) {
                console.error('Error removing duplicates:', error);
                setError('An error occurred while removing duplicates');
              }
            }}
            className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 flex items-center gap-2"
            title="Remove duplicate facilities"
          >
            <Trash2 className="w-4 h-4" />
            Clean Duplicates
          </button>
        </div>
        <div className="p-6">
          {facilities.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No facilities found</p>
          ) : (
            <div className="space-y-4">
              {facilities.map((facility) => (
                <div
                  key={facility.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{facility.name}</h4>
                        {facility.facilityTypes?.map((type) => (
                          <span
                            key={type}
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              type === 'ndis' ? 'bg-green-100 text-green-800' :
                              type === 'agedcare' ? 'bg-purple-100 text-purple-800' :
                              'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {type === 'ndis' ? 'NDIS' : type === 'agedcare' ? 'Aged Care' : type}
                          </span>
                        ))}
                      </div>
                      
                      {/* Address */}
                      {facility.address && (
                        <div className="flex items-start gap-2 mb-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mt-0.5" />
                          <span>
                            {facility.address.fullAddress || 
                             `${facility.address.street}, ${facility.address.city} ${facility.address.state} ${facility.address.postcode}`}
                          </span>
                        </div>
                      )}
                      
                      {/* Contact */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-2">
                        {facility.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            <span>{facility.phone}</span>
                          </div>
                        )}
                        {facility.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            <span>{facility.email}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* NDIS Info */}
                      {facility.ndis?.enabled && (
                        <div className="mt-2 p-2 bg-green-50 rounded-lg">
                          <p className="text-xs font-medium text-green-900 mb-1">NDIS Provider</p>
                          <p className="text-xs text-green-700">
                            {facility.ndis.ndisProviderNumber} • {getNDISTypeLabel(facility.ndis.ndisType)}
                          </p>
                        </div>
                      )}
                      
                      {/* Aged Care Info */}
                      {facility.agedCare?.enabled && (
                        <div className="mt-2 p-2 bg-purple-50 rounded-lg">
                          <p className="text-xs font-medium text-purple-900 mb-1">Aged Care Provider</p>
                          <p className="text-xs text-purple-700">
                            {facility.agedCare.providerNumber} • {getAgedCareTypeLabel(facility.agedCare.agedCareType)} • {facility.agedCare.beds} beds
                          </p>
                        </div>
                      )}
                      
                      {/* Business Group */}
                      {facility.businessGroup?.name && (
                        <div className="mt-2 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-indigo-600" />
                          <span className="text-xs text-gray-600">{facility.businessGroup.name}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(facility)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(facility.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Wards Section */}
                  <WardManagement 
                    facilityId={facility.id} 
                    onWardChange={loadFacilities}
                    onAddWard={handleAddWard}
                    onEditWard={handleEditWard}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ward/Unit Form Modal */}
      {showWardForm && selectedFacilityForWard && (() => {
        // Get facility data - prioritize selectedFacilityForWardData, then lookup in facilities array
        const facility = selectedFacilityForWardData || facilities.find(f => f.id === selectedFacilityForWard);
        
        if (!facility) {
          // If facility not found, default to Unit (safer default)
          const terminology = { singular: 'Unit', plural: 'Units' };
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6">
                <p className="text-red-600">Facility not found. Please refresh the page.</p>
              </div>
            </div>
          );
        }
        
        // Determine facility type - check multiple possible fields
        const facilityTypes = Array.isArray(facility.facilityTypes) ? facility.facilityTypes : [];
        const primaryType = facility.primaryType || '';
        const ndisEnabled = facility.ndis?.enabled === true;
        const agedCareEnabled = facility.agedCare?.enabled === true;
        
        // Check if NDIS
        const isNDIS = primaryType === 'ndis' || 
                      facilityTypes.includes('ndis') ||
                      ndisEnabled;
        
        // Check if Aged Care
        const isAgedCare = primaryType === 'agedcare' || 
                          facilityTypes.includes('agedcare') ||
                          agedCareEnabled;
        
        // Check if In-House
        const isInHouse = primaryType === 'inhouse' || 
                         facilityTypes.includes('inhouse');
        
        // Determine terminology - In-House only = Unit, Aged Care only = Ward
        const terminology = isAgedCare && !isInHouse
          ? { singular: 'Ward', plural: 'Wards' }
          : { singular: 'Unit', plural: 'Units' }; // Default to Unit for in-house or unknown
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingWardId ? `Edit ${terminology.singular}` : `Add New ${terminology.singular}`}
                </h3>
                <button
                  onClick={() => {
                    setShowWardForm(false);
                    setEditingWardId(null);
                    setSelectedFacilityForWard(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveWard} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {terminology.singular} Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={wardFormData.name}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={isNDIS && !isAgedCare ? "e.g., Unit 1 - Independence Living" : "e.g., Ward A - Memory Care"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{terminology.singular} Code</label>
                  <input
                    type="text"
                    value={wardFormData.code}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={isNDIS && !isAgedCare ? "e.g., U1, House-1" : "e.g., WA, Ward-A"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accommodation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={wardFormData.type}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, type: e.target.value }))}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {isNDIS && !isAgedCare ? (
                      // NDIS facilities - no Ward option
                      <>
                        <option value="Room">Room</option>
                        <option value="Unit">Unit</option>
                        <option value="House">House</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Cottage">Cottage</option>
                        <option value="Villa">Villa</option>
                        <option value="Flat">Flat</option>
                        <option value="Suite">Suite</option>
                      </>
                    ) : isAgedCare && !isNDIS ? (
                      // Aged Care facilities - Ward first, then Room
                      <>
                        <option value="Ward">Ward</option>
                        <option value="Room">Room</option>
                        <option value="Suite">Suite</option>
                        <option value="Unit">Unit</option>
                        <option value="Cottage">Cottage</option>
                        <option value="Villa">Villa</option>
                      </>
                    ) : (
                      // Dual facilities - show all options
                      <>
                        <option value="Room">Room</option>
                        <option value="Ward">Ward</option>
                        <option value="Unit">Unit</option>
                        <option value="House">House</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Cottage">Cottage</option>
                        <option value="Villa">Villa</option>
                        <option value="Suite">Suite</option>
                        <option value="Flat">Flat</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={wardFormData.description}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Brief description of the ${terminology.singular.toLowerCase()}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capacity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={wardFormData.capacity}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                    required
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currently Occupied</label>
                  <input
                    type="number"
                    value={wardFormData.occupied}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, occupied: parseInt(e.target.value) || 0 }))}
                    min="0"
                    max={wardFormData.capacity}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Care Level</label>
                  <select
                    value={wardFormData.careLevel}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, careLevel: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                  <input
                    type="text"
                    value={wardFormData.specialization}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, specialization: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Dementia & Alzheimer's"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Staff Ratio</label>
                  <input
                    type="text"
                    value={wardFormData.staffRatio}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, staffRatio: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 1:5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{terminology.singular} Manager</label>
                  <input
                    type="text"
                    value={wardFormData.manager}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, manager: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Manager name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Floor</label>
                  <input
                    type="text"
                    value={wardFormData.floor}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, floor: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Ground, 1st, 2nd"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Building/Wing</label>
                  <input
                    type="text"
                    value={wardFormData.building || wardFormData.wing}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, building: e.target.value, wing: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Building or wing name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={wardFormData.status}
                    onChange={(e) => setWardFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowWardForm(false);
                    setEditingWardId(null);
                    setSelectedFacilityForWard(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Save className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingWardId ? `Update ${terminology.singular}` : `Create ${terminology.singular}`}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default FacilityManagement;

